import React, { useCallback } from 'react';
import { Spinner } from '@heroui/react';
import { Typography, UniversalTable } from '../../../../components';
import { EmptyContentClientIc } from '../../../../components/icons';
import { OffersTableProps } from '../types';
import { getTableColumns } from '../constants';
import { useTranslations } from '../../../../contexts/translation.context';
import { RevshareOffer } from '../../../../api/client/clientRevshareService';
import AccountCell from './AccountCell';
import RevshareDetailsCell from './RevshareDetailsCell';
import StatisticsCell from './StatisticsCell';
import ActionsCell from './ActionsCell';

const OffersTable: React.FC<OffersTableProps> = ({
  offers,
  isLoading,
  totalCount,
  currentPage,
  pageSize,
  onPageChange,
  onPageSizeChange,
  onOpenOffer,
  showActions = true,
  showStatus = true,
}) => {
  const { t } = useTranslations();

  const renderCell = useCallback(
    (offer: RevshareOffer, columnKey: string) => {
      switch (columnKey) {
        case 'account':
          return <AccountCell offer={offer} showStatus={showStatus} />;

        case 'revshare_details':
          return <RevshareDetailsCell offer={offer} />;

        case 'statistics':
          return <StatisticsCell offer={offer} />;

        case 'actions':
          return <ActionsCell offer={offer} onOpenOffer={onOpenOffer} />;

        default:
          return null;
      }
    },
    [onOpenOffer, showStatus]
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <UniversalTable
      data={offers}
      columns={getTableColumns(t, showActions)}
      renderCell={renderCell}
      emptyContent={
        <div className="text-center py-12">
          <div className="flex flex-col items-center gap-4">
            <EmptyContentClientIc />
            <div className="flex flex-col gap-1.5">
              <Typography variant="h6" className="text-xl font-semibold leading-7">
                {t('revshare.table.noDataFound')}
              </Typography>
              <Typography
                className="text-neutral-500 text-base font-medium leading-6"
                variant="body1"
              >
                {t('revshare.table.noData')}
              </Typography>
            </div>
          </div>
        </div>
      }
      pagination={{
        count: totalCount,
        page_size: pageSize,
        page: currentPage,
      }}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
      currentPage={currentPage}
      currentPageSize={pageSize}
    />
  );
};

export default OffersTable;
